import type { MeasurementMode } from '../planning/team-measurement';
import type { ProjectId, WorkCalendarId } from '../shared/ids';

export type ProjectStatus = 'PLANNING' | 'ACTIVE' | 'PAUSED' | 'FINISHED' | 'ARCHIVED';

export const PROJECT_STATUS_LABEL: Readonly<Record<ProjectStatus, string>> = {
  PLANNING: 'Em planejamento',
  ACTIVE: 'Ativa',
  PAUSED: 'Pausada',
  FINISHED: 'Concluída',
  ARCHIVED: 'Arquivada',
};

/**
 * Obra — raiz do escopo multiobra.
 *
 * Toda entidade operacional do sistema carrega `projectId`; nenhuma consulta assume
 * obra única (RN-001).
 */
export interface Project {
  readonly id: ProjectId;
  readonly code: string;
  readonly name: string;
  /**
   * O identificador desta obra no sistema de compras do cliente.
   *
   * Existe para que a requisição saiba onde chegar sem depender de os **nomes** baterem
   * dos dois lados. Nome casa até o dia em que alguém renomeia; id não muda.
   *
   * Ausente é o normal: obra sem integração de suprimentos simplesmente não requisita.
   * É essa ausência que impede uma obra de produção de virar destino por engano.
   */
  readonly externalId?: string;
  readonly client?: string;
  readonly address?: string;
  /**
   * Quem assina a obra tecnicamente — o nome que sai no cabeçalho do RDO.
   *
   * Texto livre, e não um vínculo com perfil de usuário: o responsável técnico é o
   * engenheiro do CREA, que costuma não operar o sistema. Amarrá-lo a um `UserId`
   * obrigaria a criar perfil para quem nunca vai entrar, e deixaria o documento sem
   * assinatura no dia em que esse perfil fosse desativado.
   */
  readonly technicalManager?: string;
  /** Define o "hoje" da obra (RN-123). */
  readonly timezone: string;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly status: ProjectStatus;
  readonly workCalendarId?: WorkCalendarId;
  /** Horizonte de alerta de risco de material, em dias (RN-066). */
  readonly riskHorizonDays: number;
  /**
   * Quantos dias corridos antes do início do serviço o material precisa estar na obra
   * (RN-344, Fase 26). Ausente é o padrão, `DEFAULT_MATERIAL_ARRIVAL_MARGIN_DAYS`. É
   * necessidade, e não prazo de compra: o prazo de entrega é de quem compra (RN-141).
   */
  readonly materialArrivalMarginDays?: number;
  /** Exigir que o conferente seja diferente de quem planejou (RN-101). Padrão: desligado. */
  readonly requireDistinctInspector: boolean;
  /**
   * Como a Conferência mede a quantidade: por pessoa (o padrão, `DEFAULT_MEASUREMENT_MODE`) ou
   * por equipe (RN-405, Fase 30). A troca vale para os dias ainda não conferidos (D3).
   */
  readonly measurementMode: MeasurementMode;
  /**
   * A obra aprovada de que esta é uma **versão de orçamento** (07/10/2026). Ausente é a obra
   * aprovada — a que a execução acompanha. Presente, esta obra é um rascunho: nasce de
   * "Duplicar obra" com o projeto e o orçamento da original (estrutura, desenhos, pranchas,
   * tipologias, traçado, peças, serviços, índices e itens do quantitativo) e sem execução
   * (RDO, apontamentos, alocações, fotos, equipe, cronograma). Edita-se e exclui-se à vontade
   * até ser **aprovada**: aí ela vira a obra que a execução acompanha, e a aprovada de antes
   * vira versão.
   */
  readonly versionOf?: ProjectId;
  /** O número da versão dentro da família — 2, 3… A aprovada não tem número. */
  readonly versionNumber?: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

/** A obra é uma versão de orçamento, e não a aprovada que a execução acompanha? */
export function isProjectVersion(project: Pick<Project, 'versionOf'>): boolean {
  return project.versionOf !== undefined;
}

/** "Residencial Vienna — versão 2": o nome que a versão nasce, editável depois. */
export function versionName(approvedName: string, versionNumber: number): string {
  return `${approvedName} — versão ${String(versionNumber)}`;
}

export function normalizeProjectCode(code: string): string {
  return code.trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR');
}

/** RN-344 — a margem de chegada padrão: uma semana antes do início do serviço (D5). */
export const DEFAULT_MATERIAL_ARRIVAL_MARGIN_DAYS = 7;

/** A margem de chegada da obra, ou a padrão quando ela não a define (RN-344). */
export function materialArrivalMarginOf(
  project: Pick<Project, 'materialArrivalMarginDays'> | undefined,
): number {
  const margem = project?.materialArrivalMarginDays;
  return margem === undefined || !Number.isInteger(margem) || margem < 0
    ? DEFAULT_MATERIAL_ARRIVAL_MARGIN_DAYS
    : margem;
}
